/**
 * useUpdateEvents hook.
 */
import Event from '@shared/classes/event';
import Rail from '@shared/classes/rail';
import { RailType } from '@shared/types/enums/rail';
import { setAsset } from '@shared/store/slices/main.slice';
import { store } from '@shared/store/store';
import { updateEvents } from '@shared/store/thunks/events.thunk';
import { useAppDispatch } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';

/**
 * Buffer added past an event's `date.end` before refreshing, so `Date.now()`
 * has definitely passed it by the time the scheduled refresh fires.
 */
const END_BUFFER_MS = 1000;

/**
 * Fallback poll interval used while every item on the rail is on a
 * mocked/placeholder event (no real `date` to key a timer off of), so
 * channels eventually pick up real EPG data once it loads.
 */
const FALLBACK_POLL_MS = 60 * 1000;

/**
 * Keeps a `RailType.EVENT` or `RailType.CHANNEL` rail's rendered data fresh
 * after mount: on a schedule, dispatches `updateEvents` and remaps each item
 * to the freshly computed `currentEvents`/`mockedEvents` entry for its
 * channel, so progress bars (and the square channels rail's current event)
 * appear/update/roll over without a full page reload.
 * No-op passthrough (returns `rail.data` unchanged) for every other rail
 * type, so it's safe for `DefaultRail` to call unconditionally.
 *
 * Reads `events.currentEvents`/`events.mockedEvents` straight from the
 * store rather than `channel.getCurrentEvent()`/`getMockedEvent()`, which
 * cache their result on the `Channel` instance forever after the first
 * resolution and would keep returning the original (possibly mocked)
 * event even after the store recomputes a fresher one.
 *
 * Dispatches `updateEvents(false)`, not `true`: `true` also updates the
 * currently tuned channel's media/route state, unrelated to this rail.
 *
 * `rail` may be `undefined` (e.g. `MainContext.channels` before it loads) —
 * hooks are still called unconditionally, this just treats it the same as
 * a rail type that doesn't refresh.
 */
export default function useUpdateEvents(rail?: Rail) {
  const dispatch = useAppDispatch();
  const isRefreshable =
    rail?.type === RailType.EVENT || rail?.type === RailType.CHANNEL;
  const [data, setData] = useState<Rail['data']>(rail?.data || []);
  const dataRef = useRef(rail?.data || []);
  // Last `rail.data` this hook itself produced (via `refresh` below), so the
  // resync effect can tell "someone else replaced `rail.data`" (e.g. category
  // filtering, which mutates it in place) apart from its own writes.
  const ownDataRef = useRef(rail?.data || []);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Resync whenever `rail.data` was replaced from outside this hook.
  useEffect(() => {
    if (!isRefreshable || rail?.data === ownDataRef.current) return;
    dataRef.current = rail?.data || [];
    ownDataRef.current = rail?.data || [];
    setData(rail?.data || []);
  }, [isRefreshable, rail?.data]);

  useEffect(() => {
    if (!isRefreshable || !rail) return undefined;
    const currentRail = rail;

    const scheduleNext = (items: Event[]) => {
      const now = Date.now();
      const soonestEnd = items.reduce<number | undefined>((soonest, item) => {
        const end = item?.date?.end;
        if (end == null || end <= now) return soonest;
        return soonest == null ? end : Math.min(soonest, end);
      }, undefined);
      const delay =
        soonestEnd != null
          ? Math.max(0, soonestEnd - now + END_BUFFER_MS)
          : FALLBACK_POLL_MS;
      timeoutRef.current = setTimeout(refresh, delay);
    };

    const refresh = () => {
      dispatch(updateEvents(false));
      const { events, main } = store.getState();
      const refreshed = ((dataRef.current || []) as Event[]).map((item) => {
        const channelId = item?.channel?.id;
        if (!channelId) return item;
        return (
          events.currentEvents[channelId] ||
          events.mockedEvents[channelId] ||
          item
        );
      });
      dataRef.current = refreshed;
      ownDataRef.current = refreshed;
      currentRail.data = refreshed;
      setData(refreshed);

      // The description panel reads `main.asset`, a snapshot set once on
      // hover/select — it isn't part of `events.currentEvents`, so it never
      // sees this refresh on its own. When this rail's focused item is the
      // one just refreshed, resync `main.asset` too so the description
      // rolls over along with the rail card instead of staying on the past
      // event.
      if (main.selection === currentRail.id) {
        const hIndex = main.layout[currentRail.id]?.hIndex || 0;
        const focusedItem = refreshed[hIndex];
        if (focusedItem && focusedItem !== main.asset) {
          dispatch(setAsset(focusedItem));
        }
      }

      scheduleNext(refreshed);
    };

    refresh();

    return () => clearTimeout(timeoutRef.current);
    // `rail` (not `rail.data`) is the dependency: this effect mutates
    // `rail.data` itself, and `.map()` always returns a new array
    // reference, so depending on `rail.data` would re-trigger on every
    // refresh and loop forever. `rail`'s identity is stable for the rail's
    // lifetime; the resync effect above handles `rail.data` being replaced
    // externally (e.g. category filtering) without touching this schedule.
  }, [isRefreshable, rail, dispatch]);

  return isRefreshable ? data : rail?.data || [];
}
